import { useCallback } from 'react';

import { createQuestionGroup, deleteQuestionGroup, updateQuestionGroup } from '@api/instant_survey';
import { toast } from '@utilities';

import { DEFAULT_QUESTION_GROUP_NAME } from './constants';
import { createQuestionKey, isQuestionGroupNameTaken } from './helpers';

/**
 * lastGroupMemberIndex
 * Index of the last question in `questions` that belongs to `groupId` or any of its child groups
 * (-1 if none). Used to keep a group's questions — and its nested groups' questions — contiguous
 * when inserting into the flat question list.
 */
const lastGroupMemberIndex = (questions, groupId, groups) => {
  if (groupId == null) return -1;
  const groupIds = new Set([groupId]);
  Object.values(groups).forEach((group) => {
    if (group.parentGroup === groupId) groupIds.add(group.id);
  });
  return questions.reduce(
    (lastIndex, question, index) => (groupIds.has(question.questionGroup) ? index : lastIndex),
    -1
  );
};

/**
 * planEmptyGroupCleanup
 * Given the post-change questions and groups, finds every group left without a direct member
 * question and returns the flattening plan for it: the groups to delete, plus the surviving child
 * groups that must be re-parented (to the deleted group's own parent, or the nearest ancestor that
 * is itself surviving) so no group is ever left pointing at a deleted parent.
 * @param {Array} questions flat post-change question list
 * @param {Object} groups questionGroups keyed by stringified id
 * @return {{ orphanIds: string[], reparents: Array<{ groupId: number, parentGroup: number|null }> }}
 */
const planEmptyGroupCleanup = (questions, groups) => {
  const occupiedGroupIds = new Set(
    questions
      .filter(({ questionGroup }) => questionGroup != null)
      .map(({ questionGroup }) => String(questionGroup))
  );
  const orphanIds = Object.keys(groups).filter((id) => !occupiedGroupIds.has(id));
  const orphanIdSet = new Set(orphanIds);

  // Nearest ancestor that isn't itself being deleted (null once we run out). The visited set guards
  // against a parentGroup cycle, which would otherwise spin forever.
  const surviving = (groupId) => {
    const visited = new Set();
    let currentId = groupId == null ? null : String(groupId);
    while (currentId != null && orphanIdSet.has(currentId) && !visited.has(currentId)) {
      visited.add(currentId);
      const parentGroup = groups[currentId]?.parentGroup;
      currentId = parentGroup == null ? null : String(parentGroup);
    }
    return currentId == null || orphanIdSet.has(currentId) ? null : Number(currentId);
  };

  const reparents = Object.values(groups)
    .filter(
      ({ id, parentGroup }) =>
        parentGroup != null && orphanIdSet.has(String(parentGroup)) && !orphanIdSet.has(String(id))
    )
    .map(({ id, parentGroup }) => ({ groupId: id, parentGroup: surviving(parentGroup) }));

  return { orphanIds, reparents };
};

/**
 * flattenEmptyGroups
 * Commits `questions` + the flattening plan for any group they leave empty: optimistically updates
 * survey state, then re-parents surviving children before deleting the emptied groups on the
 * backend. Anything the backend still holds after a failure is restored to local state so the two
 * don't drift.
 * @param {Object} params
 * @param {Object} params.groups questionGroups keyed by stringified id, post-change
 * @param {boolean} params.markDirty whether the question-list change should mark the draft dirty
 * @param {Array} params.questions flat post-change question list
 * @param {Object} params.surveyRef ref whose `.current` holds the live survey
 * @param {Function} params.updateSurvey survey state updater
 */
const flattenEmptyGroups = async ({ groups, markDirty, questions, surveyRef, updateSurvey }) => {
  const { orphanIds, reparents } = planEmptyGroupCleanup(questions, groups);

  const nextGroups = { ...groups };
  reparents.forEach(({ groupId, parentGroup }) => {
    nextGroups[String(groupId)] = { ...groups[String(groupId)], parentGroup };
  });
  orphanIds.forEach((id) => delete nextGroups[id]);

  updateSurvey({ questionGroups: nextGroups, questions }, { markDirty });

  if (orphanIds.length === 0) return;

  const reparentResults = await Promise.allSettled(
    reparents.map(({ groupId, parentGroup }) => updateQuestionGroup({ id: groupId, parentGroup }))
  );
  const failedReparents = reparents.filter(
    (_, index) => reparentResults[index].status === 'rejected'
  );
  // A group whose child could not be re-parented has to stay — deleting it would orphan that child.
  const blockedOrphanIds = new Set(
    failedReparents.map(({ groupId }) => String(groups[String(groupId)]?.parentGroup))
  );
  const deletableOrphanIds = orphanIds.filter((id) => !blockedOrphanIds.has(id));

  const deleteResults = await Promise.allSettled(
    deletableOrphanIds.map((id) => deleteQuestionGroup({ id: Number(id) }))
  );
  const failedDeleteIds = deletableOrphanIds.filter(
    (id, index) => deleteResults[index].status === 'rejected'
  );

  const retainedGroupIds = [
    ...blockedOrphanIds,
    ...failedDeleteIds,
    ...failedReparents.map(({ groupId }) => String(groupId)),
  ];
  if (retainedGroupIds.length === 0) return;

  updateSurvey(
    {
      questionGroups: {
        ...(surveyRef.current.questionGroups || {}),
        ...Object.fromEntries(retainedGroupIds.map((id) => [id, groups[id]])),
      },
    },
    { markDirty: false }
  );
  toast.error('Unable to delete an empty question group. Please refresh and try again.');
};

/**
 * useQuestionGroupHelpers
 * Question group create/rename/randomize/assign/nest callbacks. Read survey state via surveyRef so
 * callbacks don't need survey in deps.
 * @param {Object} params
 * @param {Object} params.surveyRef ref whose `.current` holds the live survey
 * @param {Function} params.updateSurvey survey state updater
 * @return {{
 *   assignGroupToParent: Function,
 *   assignQuestionToGroup: Function,
 *   createGroupForQuestion: Function,
 *   renameGroup: Function,
 *   toggleGroupRandomize: Function,
 *   toggleQuestionAnchored: Function,
 * }}
 */
export const useQuestionGroupHelpers = ({ surveyRef, updateSurvey }) => {
  const createGroupForQuestion = useCallback(
    async (question) => {
      const targetKey = createQuestionKey(question);

      // Groups are created without a name prompt, so the name has to be unique by construction.
      let name = DEFAULT_QUESTION_GROUP_NAME;
      let nameSuffix = 2;
      while (isQuestionGroupNameTaken({ name, questionGroups: surveyRef.current.questionGroups })) {
        name = `${DEFAULT_QUESTION_GROUP_NAME} ${nameSuffix}`;
        nameSuffix += 1;
      }

      const group = await createQuestionGroup({ name });
      updateSurvey(
        {
          questionGroups: {
            ...(surveyRef.current.questionGroups || {}),
            [String(group.id)]: group,
          },
          questions: surveyRef.current.questions.map((q) =>
            createQuestionKey(q) === targetKey ? { ...q, questionGroup: group.id } : q
          ),
        },
        { markDirty: true }
      );
    },
    [surveyRef, updateSurvey]
  );

  const renameGroup = useCallback(
    async (groupId, name) => {
      await updateQuestionGroup({ id: groupId, name });
      updateSurvey(
        {
          questionGroups: {
            ...(surveyRef.current.questionGroups || {}),
            [String(groupId)]: {
              ...(surveyRef.current.questionGroups?.[String(groupId)] || { id: groupId }),
              name,
            },
          },
        },
        { markDirty: false }
      );
    },
    [surveyRef, updateSurvey]
  );

  const toggleGroupRandomize = useCallback(
    async (groupId) => {
      const existing = surveyRef.current.questionGroups?.[String(groupId)];
      const nextRandomize = !(existing?.randomizeOrder ?? false);
      await updateQuestionGroup({ id: groupId, randomizeOrder: nextRandomize });
      updateSurvey(
        {
          questionGroups: {
            ...(surveyRef.current.questionGroups || {}),
            [String(groupId)]: {
              ...(existing || { id: groupId }),
              randomizeOrder: nextRandomize,
            },
          },
        },
        { markDirty: false }
      );
    },
    [surveyRef, updateSurvey]
  );

  // Nest a group under a parent (parentGroupId) or detach it (null). When nesting, the group's
  // questions are repositioned directly after the parent group's run so the flat order stays
  // contiguous and buildRenderList can derive the nested tree.
  const assignGroupToParent = useCallback(
    async (groupId, parentGroupId) => {
      const updatedGroup = await updateQuestionGroup({
        id: groupId,
        parentGroup: parentGroupId,
      });
      const latestGroups = surveyRef.current.questionGroups || {};
      const latestQuestions = surveyRef.current.questions;
      const movingQuestions = latestQuestions.filter(
        ({ questionGroup }) => questionGroup === groupId
      );
      let updatedQuestions = latestQuestions;

      if (parentGroupId != null && movingQuestions.length > 0) {
        const withoutMovingQuestions = latestQuestions.filter(
          ({ questionGroup }) => questionGroup !== groupId
        );
        const lastParentQuestionIndex = lastGroupMemberIndex(
          withoutMovingQuestions,
          parentGroupId,
          latestGroups
        );
        const insertAt =
          lastParentQuestionIndex === -1
            ? withoutMovingQuestions.length
            : lastParentQuestionIndex + 1;
        updatedQuestions = [...withoutMovingQuestions];
        updatedQuestions.splice(insertAt, 0, ...movingQuestions);
      }

      await flattenEmptyGroups({
        groups: {
          ...latestGroups,
          [String(groupId)]: updatedGroup,
        },
        markDirty: updatedQuestions !== latestQuestions,
        questions: updatedQuestions,
        surveyRef,
        updateSurvey,
      });
    },
    [surveyRef, updateSurvey]
  );

  const toggleQuestionAnchored = useCallback(
    (question) => {
      const targetQuestionKey = createQuestionKey(question);
      updateSurvey(
        {
          questions: surveyRef.current.questions.map((currentQuestion) =>
            createQuestionKey(currentQuestion) === targetQuestionKey
              ? { ...currentQuestion, anchored: !currentQuestion.anchored }
              : currentQuestion
          ),
        },
        { markDirty: true }
      );
    },
    [surveyRef, updateSurvey]
  );

  // Move a question into a group (groupId) or out of its group (null), repositioning it so group
  // boundaries stay contiguous:
  //  - into a group  → inserted as the group's LAST member;
  //  - out of a group → moved to directly below its old group's run (the question is kept).
  // Groups left without a direct member question are flattened away: surviving child groups are
  // re-parented up a level and the emptied group is deleted.
  const assignQuestionToGroup = useCallback(
    async (question, groupId) => {
      const targetKey = createQuestionKey(question);
      const questions = surveyRef.current.questions;
      const moving = questions.find((q) => createQuestionKey(q) === targetKey);
      if (!moving) return;
      const oldGroupId = moving.questionGroup ?? null;
      const originalIndex = questions.findIndex((q) => createQuestionKey(q) === targetKey);

      const without = questions.filter((q) => createQuestionKey(q) !== targetKey);
      const currentGroups = surveyRef.current.questionGroups || {};

      let insertAt;
      if (groupId != null) {
        const lastIdx = lastGroupMemberIndex(without, groupId, currentGroups);
        insertAt = lastIdx === -1 ? without.length : lastIdx + 1;
      } else {
        // Ungrouping: drop directly below the old group's run; if that group is now empty (or the
        // question had no group), keep it where it was.
        const lastIdx = lastGroupMemberIndex(without, oldGroupId, currentGroups);
        insertAt = lastIdx === -1 ? Math.min(originalIndex, without.length) : lastIdx + 1;
      }

      const updatedQuestions = [...without];
      updatedQuestions.splice(insertAt, 0, {
        ...moving,
        anchored: groupId == null ? false : moving.anchored,
        questionGroup: groupId,
      });

      await flattenEmptyGroups({
        groups: currentGroups,
        markDirty: true,
        questions: updatedQuestions,
        surveyRef,
        updateSurvey,
      });
    },
    [surveyRef, updateSurvey]
  );

  return {
    assignGroupToParent,
    assignQuestionToGroup,
    createGroupForQuestion,
    renameGroup,
    toggleGroupRandomize,
    toggleQuestionAnchored,
  };
};
